import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBillingSession, getMyCredits } from '../services/api';

type BillingResultPanelProps = {
  status: 'success' | 'cancel';
};

function formatCredits(value: unknown): string {
  const number = Number(value || 0);
  return Number.isFinite(number) ? new Intl.NumberFormat('zh-CN').format(number) : '0';
}

function formatCurrency(cents: unknown, currency: unknown): string {
  const number = Number(cents || 0) / 100;
  const code = String(currency || 'usd').toUpperCase();
  return `${Number.isFinite(number) ? number.toFixed(2) : '0.00'} ${code}`;
}

export default function BillingResultPanel({ status }: BillingResultPanelProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [loading, setLoading] = useState(Boolean(sessionId && status === 'success'));
  const [error, setError] = useState('');
  const [session, setSession] = useState<Record<string, any> | null>(null);
  const [credits, setCredits] = useState<Record<string, any> | null>(null);

  const payment = (session?.payment || null) as Record<string, any> | null;
  const stripe = (session?.stripe || null) as Record<string, any> | null;
  const paid = payment?.status === 'paid' || stripe?.paymentStatus === 'paid';
  const headline = useMemo(() => {
    if (status === 'cancel') return '支付已取消';
    if (paid) return '支付已完成';
    return '正在等待支付确认';
  }, [paid, status]);

  const loadStatus = useCallback(async () => {
    if (!sessionId || status !== 'success') return;
    setLoading(true);
    setError('');
    try {
      const [sessionResult, creditsResult] = await Promise.all([
        getBillingSession(sessionId),
        getMyCredits(20),
      ]);
      setSession(sessionResult);
      setCredits(creditsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId, status]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  return (
    <div className="account-shell">
      <nav className="top-nav">
        <div className="nav-left">
          <h1 className="nav-brand">Academic Cluster</h1>
          <span className="nav-subtitle">Billing</span>
        </div>
        <div className="nav-right">
          <button className="nav-btn" onClick={() => navigate('/account')}>账户</button>
          <button className="nav-btn" onClick={() => navigate('/')}>工作台</button>
        </div>
      </nav>

      <main className="account-main">
        <section className="account-hero">
          <div>
            <p className="route-kicker">Stripe Checkout</p>
            <h2>{headline}</h2>
            <p>
              {status === 'cancel'
                ? '本次 Stripe Checkout 没有完成扣款，额度不会变化。'
                : '支付成功后由 Stripe webhook 入账。若这里暂未显示 paid，请稍等几秒再刷新。'}
            </p>
          </div>
          <div className="account-token-badge">
            <span>Session</span>
            <strong>{sessionId || '-'}</strong>
          </div>
        </section>

        {(error || loading) && (
          <div className={error ? 'account-alert account-alert-error' : 'account-alert'}>
            {error || '正在查询支付状态...'}
          </div>
        )}

        <section className="account-grid">
          <div className="account-card">
            <p className="route-kicker">Payment</p>
            <h3>{payment?.status || stripe?.paymentStatus || status}</h3>
            <div className="account-list">
              <div><span>金额</span><strong>{formatCurrency(payment?.amount_cents ?? stripe?.amountTotal, payment?.currency ?? stripe?.currency)}</strong></div>
              <div><span>Credits</span><strong>{formatCredits(payment?.credits ?? stripe?.credits)}</strong></div>
              <div><span>Provider</span><strong>{session?.provider || 'stripe'}</strong></div>
            </div>
          </div>

          <div className="account-card">
            <p className="route-kicker">Account</p>
            <h3>{formatCredits((credits?.credits as Record<string, any> | undefined)?.availableCredits)} credits</h3>
            <div className="account-list">
              <div><span>余额</span><strong>{formatCredits((credits?.credits as Record<string, any> | undefined)?.balanceCredits)}</strong></div>
              <div><span>预扣</span><strong>{formatCredits((credits?.credits as Record<string, any> | undefined)?.reservedCredits)}</strong></div>
            </div>
          </div>

          <div className="account-card account-card-wide">
            <div className="account-card-head">
              <div>
                <p className="route-kicker">Webhook</p>
                <h3>入账状态</h3>
              </div>
              <button className="manage-secondary" onClick={() => void loadStatus()} disabled={loading || !sessionId}>刷新</button>
            </div>
            <div className="account-table">
              <div className="account-table-row">
                <span>Local</span>
                <strong>{payment ? payment.status : 'missing'}</strong>
                <span>{payment?.id || '-'}</span>
                <small>{payment ? 'D1 payment record exists' : 'Webhook 可能尚未完成或当前用户无权查看该 session'}</small>
              </div>
              <div className="account-table-row">
                <span>Stripe</span>
                <strong>{stripe?.status || stripe?.paymentStatus || '-'}</strong>
                <span>{stripe?.id || sessionId || '-'}</span>
                <small>{stripe?.error || 'Checkout Session lookup'}</small>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
